import React, { useState, useRef } from "react";
import { css } from "@emotion/react";
import { colors, fontSize, margins } from "style";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { Col, Row } from "components/Containers";
import { Spinner } from "components/Loader";
import Icon, { ICON_SIZE, ICON_TYPE } from "components/Icon";
import { FileUpload } from "components/Form";
import { ActionButton } from "components/Buttons";

/**
 * ProfileResumeAutoFillDropzone
 *
 * @param {Object}      task
 * @param {Boolean}     disabled
 * @param {Boolean}     creating
 * @param {String}      disabledMessageId
 * @param {Function}    onUpload
 * @param {Function}    setShowUploader
 */
const ProfileResumeAutoFillDropzone = ({
  task,
  disabled,
  creating,
  onUpload,
  setShowUploader,
  disabledMessageId = "Profile.ProfileResumeAutoFill.DisabledMessage",
}) => {
  const hasTask = Boolean(task);
  const inputRef = useRef();
  const [isDragging, setIsDragging] = useState(false);

  const handleCancelDifferentResume = () => {
    setShowUploader(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();

    if (disabled || creating) return;

    setIsDragging(false);

    handleFile(event.dataTransfer.files?.[0]);
  };

  const handleFile = async (selectedFile) => {
    if (!selectedFile || disabled) return;

    const started = await onUpload(selectedFile);

    if (started) {
      setShowUploader(false);
    }
  };

  return (
    <>
      <Row
        css={styles.dropzone(isDragging, disabled || creating)}
        onClick={() => {
          if (!disabled && !creating) {
            inputRef.current?.openFileDialog();
          }
        }}
        onDragEnter={() => {
          if (!disabled && !creating) {
            setIsDragging(true);
          }
        }}
        onDragLeave={() => setIsDragging(false)}
        onDragOver={(event) => event.preventDefault()}
        onDrop={handleDrop}
        role="button"
        aria-disabled={disabled || creating}
        tabIndex={disabled || creating ? -1 : 0}
        onKeyDown={(event) => {
          if (!disabled && !creating && (event.key === "Enter" || event.key === " ")) {
            inputRef.current?.openFileDialog();
          }
        }}
      >
        {creating ? (
          <>
            <Spinner size="2.6rem" margin="0" />

            <Col gap={margins.xxxsmall}>
              <strong css={styles.upload_title(disabled)}>
                <FormattedMessage id="Profile.ProfileResumeAutoFill.UploadingTitle" />
              </strong>

              <span css={styles.secondary_text}>
                <FormattedMessage id="Profile.ProfileResumeAutoFill.UploadingDescription" />
              </span>
            </Col>
          </>
        ) : (
          <>
            <Icon
              type={ICON_TYPE.upload}
              size={ICON_SIZE.xxxlarge}
              color={disabled ? colors.grayAnatomyLight1 : colors.purpleRainBase}
            />

            <Col gap={margins.xxxsmall}>
              <strong css={styles.upload_title(disabled)}>
                {disabled ? (
                  <FormattedMessage id="Profile.ProfileResumeAutoFill.DisabledTitle" />
                ) : hasTask ? (
                  <FormattedMessage id="Profile.ProfileResumeAutoFill.UploadDifferentResume" />
                ) : (
                  <FormattedMessage id="Profile.ProfileResumeAutoFill.UploadResume" />
                )}
              </strong>

              <span css={styles.secondary_text}>
                {disabled ? (
                  <FormattedMessage id={disabledMessageId} />
                ) : (
                  <FormattedMessage id="Profile.ProfileResumeAutoFill.DropzoneDescription" />
                )}
              </span>

              {!disabled && (
                <span css={styles.metadata}>
                  <FormattedMessage id="Profile.ProfileResumeAutoFill.FileRequirements" />
                </span>
              )}
            </Col>
          </>
        )}
      </Row>

      {!disabled && !creating && (
        <FileUpload
          ref={inputRef}
          name="resume"
          accept="application/pdf"
          onChange={(_name, selectedFile) => handleFile(selectedFile)}
          hideTrigger
          showFilePreview={false}
        />
      )}

      {hasTask && !creating && !disabled && (
        <Row css={styles.cancel_upload}>
          <ActionButton type="button" onClick={handleCancelDifferentResume}>
            <FormattedMessage id="Profile.ProfileResumeAutoFill.Cancel" />
          </ActionButton>
        </Row>
      )}
    </>
  );
};

const styles = {
  dropzone: (isDragging, disabled) => css`
    justify-content: center;
    min-height: 12rem;
    padding: ${margins.xsmall};
    border: 2px dashed
      ${disabled ? colors.grayAnatomyLight3 : isDragging ? colors.purpleRainBase : colors.grayAnatomyLight1};
    border-radius: 0.8rem;
    background: ${disabled ? colors.grayAnatomyLight5 : isDragging ? colors.grayAnatomyLight5 : colors.white};
    cursor: ${disabled ? "not-allowed" : "pointer"};
    text-align: center;
    opacity: ${disabled ? 0.75 : 1};
    transition: border-color 0.2s ease, background 0.2s ease;

    ${!disabled &&
    css`
      &:hover {
        border-color: ${colors.purpleRainBase};
        background: ${colors.grayAnatomyLight5};
      }
    `}

    @container (max-width: 36rem) {
      flex-direction: column;
      align-items: center;
    }
  `,
  upload_title: (disabled = false) => css`
    color: ${disabled ? colors.grayAnatomyBase : colors.purpleRainBase};
    font-size: ${fontSize.small};
    font-weight: 600;
  `,
  secondary_text: css`
    color: ${colors.grayAnatomyBase};
    font-size: ${fontSize.xsmall};
  `,
  metadata: css`
    color: ${colors.grayAnatomyLight1};
    font-size: ${fontSize.xxsmall};
    line-height: 1.4;
  `,
  cancel_upload: css`
    justify-content: flex-end;
  `,
};

ProfileResumeAutoFillDropzone.propTypes = {
  task: PropTypes.object,
  disabled: PropTypes.bool,
  creating: PropTypes.bool,
  disabledMessageId: PropTypes.string,
  onUpload: PropTypes.func.isRequired,
  setShowUploader: PropTypes.func.isRequired,
};

export default ProfileResumeAutoFillDropzone;
